// AppSelect.jsx
// App-standard dropdown. A thin, drop-in wrapper over react-select that exists
// for one reason: react-select renders its menu INLINE by default, and almost
// every one of our selects lives inside an AppModal.
//
// AppModal is a MUI <Dialog scroll="paper">, so its DialogContent is an
// `overflow: auto` scroll box. An inline menu is clipped at that box's edge —
// for fields low in a form the menu is effectively unusable, which is the
// "dropdown is cut off / hidden behind the popup" class of bug.
//
// The fix is to portal the menu to <body> and give it a z-index above the
// dialog. That's two props that were being forgotten at ~140 call sites, so
// they're baked in here instead. This is the same move we already made for
// AppModal (dialog shell) and AppDateTimePicker (calendar) — every widget that
// got a shared wrapper had its layering solved once and stayed solved.
//
// Drop-in by design. Everything is passed straight through to react-select, so
// migrating a call site is just the import line:
//
//   - import Select from "react-select";
//   + import Select from "../../Common/AppSelect";
//
// Escape hatches:
//   * menuPortalTarget={null}  — opt out of portalling (e.g. a custom
//     components={{ Menu }} that renders in flow and isn't positioned).
//   * styles={{ menuPortal: ... }} — your fn receives the base with our
//     z-index already applied, so you extend rather than clobber it.

import React from "react";
import PropTypes from "prop-types";
import ReactSelect from "react-select";
import Z_INDEX from "../zIndexScale";

// Above the dialog and the calendar so the menu is reachable, but deliberately
// BELOW snackbar/tooltip so toasts and validation tips still surface over an
// open menu. The whole scale (and why) lives in Components/Common/zIndexScale.js.
export const SELECT_MENU_Z_INDEX = Z_INDEX.menu;

// `undefined` means "caller said nothing" → portal to body. An explicit `null`
// means "caller deliberately opted out" → leave the menu inline.
const resolvePortalTarget = (target) => {
  if (target !== undefined) return target;
  return typeof document !== "undefined" ? document.body : null;
};

export default function AppSelect({ styles, menuPortalTarget, ...selectProps }) {
  const mergedStyles = {
    ...styles,
    menuPortal: (base, state) => {
      const withZIndex = { ...base, zIndex: SELECT_MENU_Z_INDEX };
      // Let a call-site layer its own tweaks on top of our z-index rather than
      // replacing the whole rule (the old hand-rolled sites all just set
      // zIndex, so they lost nothing by having it applied for them).
      return styles?.menuPortal ? styles.menuPortal(withZIndex, state) : withZIndex;
    },
  };

  // Deliberately NOT setting `menuShouldBlockScroll`. It looks like the right
  // fix for a portalled menu drifting when the dialog behind it scrolls, but
  // react-select implements it by mutating document.body's `overflow` and
  // `paddingRight` — exactly the styles MUI's Dialog scroll-lock already owns.
  // Two lock managers on the same styles can unwind out of order (e.g. the
  // modal closes while a menu is open) and strand the page at
  // `overflow: hidden`. MUI already blocks background scroll inside a dialog,
  // so this would buy nothing and risk that. A call-site can still opt in.
  return (
    <ReactSelect
      // Position the (body-portalled) menu with `position: fixed` off the
      // control's live viewport rect. The default `absolute` mode offsets the
      // menu against the document, which lands wrong inside a MUI Dialog (its
      // transition/stacking context shifts the origin) — the menu detaches to a
      // screen corner, intermittently, depending on scroll/animation timing.
      // `fixed` is react-select's mode for exactly this (selects in scrolling /
      // modal containers) and it re-tracks the control on scroll. Overridable.
      menuPosition="fixed"
      {...selectProps}
      menuPortalTarget={resolvePortalTarget(menuPortalTarget)}
      styles={mergedStyles}
    />
  );
}

AppSelect.propTypes = {
  // Standard react-select styles object. `menuPortal` is merged, not replaced.
  styles: PropTypes.object,
  // Pass null to opt out of portalling and keep the menu inline.
  menuPortalTarget: PropTypes.any,
};
